/**
 * Login / account sub-FAB for SwissPass authentication.
 *
 * Two mutually exclusive modes driven by the persisted auth slice:
 *   - signed out — opens a MiniModal with the SwissPass login CTA;
 *   - signed in — opens a MiniModal with a personalized greeting and logout.
 *
 * Burst plumbing (anchor, panel close, a11y) is provided by
 * {@link SubFloatingAction}; this file owns session handlers and CTA chrome.
 */

import { useIonToast } from "@ionic/react";
import React, { useCallback, useMemo, useState } from "react";
import LoginIcon from "../../../../image/icons/auth/LoginIcon.svg";
import PersonIcon from "../../../../image/icons/auth/PersonIcon.svg";
import {
    clearAuthSession,
    getAccessTokenFirstname,
    logoutSession,
    peekAuthState,
    startSwissPassLoginWithStateSave,
} from "../../../../services/auth";
import { authSlice, usePersistedSlice } from "../../../../store/persistence";
import { getUIColorPalette } from "../../../../theme/ColorPalette";
import { BURST_SUB_FAB_GREEN_FILTER } from "../../floatingAction.configuration";
import SubFloatingAction from "./SubFloatingAction";

/** Neutral dark fill for the logout button (no SwissPass branding). */
const LOGOUT_BUTTON_BG = "rgba(60, 60, 60, 0.92)";

/** Props exposed by the auth sub-FAB. */
export interface LoginSubFloatingActionProps {

    /** When `true`, use the dark palette variants. */
    "isDarkUi": boolean;
}

/**
 * Burst sub-FAB that switches between login and account UI from auth state.
 *
 * @param props Component props. See {@link LoginSubFloatingActionProps}.
 * @returns Configured modal sub-FAB for the current session state.
 */
const LoginSubFloatingAction: React.FC<LoginSubFloatingActionProps> = ( { isDarkUi } ) => {
    const [ auth ] = usePersistedSlice( authSlice );
    const [ present ] = useIonToast();
    const [ isLoggingIn, setIsLoggingIn ] = useState( false );
    const [ isLoggingOut, setIsLoggingOut ] = useState( false );
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );

    const isAuthenticated = auth.accessToken != null && auth.accessToken !== "";
    const firstname = isAuthenticated && auth.accessToken != null
        ? getAccessTokenFirstname( auth.accessToken )
        : null;
    const accountTitle = firstname == null ? "Bonjour" : `Bonjour ${ firstname }`;

    /**
     * Shows a short bottom toast for auth success / failure feedback.
     *
     * @param message Toast body text.
     * @param color Ionic toast color token.
     */
    const presentBottomToast = useCallback( ( message: string, color: "success" | "danger" ): void => {
        void present( {
            message,
            color,
            "duration": 2500,
            "position": "bottom",
        } );
    }, [ present ] );

    /**
     * Starts the SwissPass OAuth redirect flow.
     *
     * @remarks On success the page navigates away, so `isLoggingIn` is left
     * `true` intentionally. Failure clears the busy flag and surfaces a toast.
     */
    const handleSwissPassLogin = useCallback( async(): Promise<void> => {
        if ( isLoggingIn ) {
            return;
        }

        setIsLoggingIn( true );
        try {
            await startSwissPassLoginWithStateSave();
        } catch( error: unknown ) {
            setIsLoggingIn( false );
            const message = error instanceof Error
                ? error.message
                : "Impossible de démarrer la connexion SwissPass";
            presentBottomToast( message, "danger" );
        }
    }, [ isLoggingIn, presentBottomToast ] );

    /**
     * Ends the backend session when possible, then clears local auth state.
     *
     * @remarks A `401` from logout is treated as success: the token is already
     * invalid server-side, so the local session is still wiped.
     */
    const handleLogout = useCallback( async(): Promise<void> => {
        if ( isLoggingOut ) {
            return;
        }

        setIsLoggingOut( true );
        const accessToken = peekAuthState().accessToken;

        try {
            if ( accessToken != null && accessToken !== "" ) {
                await logoutSession( accessToken );
            }
            clearAuthSession();
            presentBottomToast( "Déconnexion réussie", "success" );
        } catch( error: unknown ) {
            const status = typeof error === "object" &&
              error != null &&
              "response" in error &&
              typeof ( error as { "response"?: { "status"?: number } } ).response?.status === "number"
                ? ( error as { "response": { "status": number } } ).response.status
                : null;

            // Expired / already-revoked tokens still warrant a local logout.
            if ( status === 401 ) {
                clearAuthSession();
                presentBottomToast( "Déconnexion réussie", "success" );
            } else {
                presentBottomToast( "La déconnexion a échoué. Réessayez.", "danger" );
            }
        } finally {
            setIsLoggingOut( false );
        }
    }, [ isLoggingOut, presentBottomToast ] );

    if ( isAuthenticated ) {
        return (
            <SubFloatingAction
                ariaLabel="Compte"
                iconAlt="Compte"
                iconFilter={BURST_SUB_FAB_GREEN_FILTER}
                iconSrc={PersonIcon}
                isDarkUi={isDarkUi}
                kind="modal"
                modalTitle={accountTitle}
            >
                <button
                    className="w-full rounded-full px-4 py-2.5 text-sm font-medium text-white transition-colors"
                    disabled={isLoggingOut}
                    onClick={() => {
                        void handleLogout();
                    }}
                    style={{ "backgroundColor": LOGOUT_BUTTON_BG }}
                    type="button"
                >
                    {isLoggingOut ? "Déconnexion…" : "Déconnexion"}
                </button>
            </SubFloatingAction>
        );
    }

    return (
        <SubFloatingAction
            ariaLabel="Connexion"
            iconAlt="Connexion"
            iconFilter={BURST_SUB_FAB_GREEN_FILTER}
            iconSrc={LoginIcon}
            isDarkUi={isDarkUi}
            kind="modal"
            modalTitle="Connexion"
        >
            <button
                className="w-full rounded-full px-4 py-2.5 text-sm font-medium text-white transition-colors"
                disabled={isLoggingIn}
                onClick={() => {
                    void handleSwissPassLogin();
                }}
                onMouseEnter={( event ) => {
                    event.currentTarget.style.backgroundColor = palette.swissPassButtonBackgroundHover;
                }}
                onMouseLeave={( event ) => {
                    event.currentTarget.style.backgroundColor = palette.swissPassButtonBackground;
                }}
                style={{ "backgroundColor": palette.swissPassButtonBackground }}
                type="button"
            >
                {isLoggingIn ? "Redirection…" : "Se connecter avec SwissPass"}
            </button>
        </SubFloatingAction>
    );
};

export default LoginSubFloatingAction;
